import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle2, MessageCircle, Sparkles } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { useToast } from '../../hooks/use-toast';
import { COMPANY, DESTINATIONS, saveEnquiry } from '../../mock';

export default function EnquiryForm({ compact = false }) {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: '', phone: '', email: '', destination: '', date: '', people: '2', budget: '15000-25000' });

  const update = (k, v) => setForm(s => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.destination) {
      toast({ title: 'Please fill required fields', description: 'Name, phone and destination are required.' });
      return;
    }
    await saveEnquiry(form);
    navigate('/thank-you', { state: form });
  };

  return (
    <section id="enquiry" className="max-w-7xl mx-auto px-6 py-20 lg:py-24">
      <div className="grid lg:grid-cols-5 gap-10 items-center">
        {!compact && (
          <div className="lg:col-span-2">
            <div className="text-brand-orange text-[13px] font-semibold tracking-[0.18em] uppercase mb-2">Plan Your Trip</div>
            <h2 className="font-poppins text-[26px] sm:text-[34px] lg:text-[42px] text-brand-blue leading-[1.1]">Get your <span className="text-brand-orange">best quote</span> instantly</h2>
            <p className="text-brand-blue/65 mt-4 text-[15px] leading-relaxed">Share a few details. A real travel designer (not a bot) will craft a personalized itinerary and share it on WhatsApp - absolutely free, no obligation.</p>
            <ul className="mt-6 space-y-3">
              {['No-cost cancellation up to 14 days', 'Best-price guarantee - we will beat any quote', 'Pay 20% to book, rest before departure', 'Hand-picked verified hotels only'].map((t, i) => (
                <li key={i} className="flex items-start gap-3 text-[14px] text-brand-blue/80"><CheckCircle2 size={18} className="text-brand-orange shrink-0 mt-0.5" />{t}</li>
              ))}
            </ul>
            <a href={`https://wa.me/${COMPANY.whatsapp}?text=${encodeURIComponent('Hi Mountains Chain Travel! 👋\n\nI\'d love to plan a trip with you.\n\nPlease share:\n- Available tour packages\n- Customizable itineraries\n- Best deals & offers\n\nThank you!')}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 mt-7 text-brand-blue font-semibold border-b-2 border-brand-orange pb-1 hover:text-brand-orange transition-colors">
              <MessageCircle size={18} /> Or chat with us on WhatsApp
            </a>
          </div>
        )}

        <div className={compact ? 'lg:col-span-5' : 'lg:col-span-3'}>
          <form onSubmit={submit} className="bg-white rounded-3xl shadow-premium border border-brand-greyDark p-7 lg:p-9 relative overflow-hidden">
            <div className="absolute -top-16 -right-16 w-48 h-48 rounded-full bg-brand-orange/10" />
            <div className="absolute -bottom-16 -left-10 w-40 h-40 rounded-full bg-brand-blue/5" />
            <div className="relative">
              <div className="flex items-center gap-2 mb-1">
                <Sparkles size={18} className="text-brand-orange" />
                <span className="text-brand-orange text-[12px] font-bold tracking-wider uppercase">Free Consultation</span>
              </div>
              <h3 className="font-poppins font-bold text-brand-blue text-[24px] mb-5">Tell us about your dream trip</h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Your Name *"><Input value={form.name} onChange={e => update('name', e.target.value)} placeholder="e.g., Rohan Sharma" className="h-11 rounded-lg" /></Field>
                <Field label="Phone Number *"><Input value={form.phone} onChange={e => { const v = e.target.value.replace(/\D/g, '').slice(0, 10); update('phone', v); }} placeholder="9XXXXXXXXX" maxLength={10} className="h-11 rounded-lg" inputMode="numeric" /></Field>
                <Field label="Email"><Input type="email" value={form.email} onChange={e => update('email', e.target.value)} placeholder="you@example.com" className="h-11 rounded-lg" /></Field>
                <Field label="Destination *">
                  <select value={form.destination} onChange={e => update('destination', e.target.value)} className="w-full h-11 rounded-lg border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand-orange/40">
                    <option value="">Choose destination</option>
                    {DESTINATIONS.map(d => <option key={d.slug} value={d.name}>{d.name}</option>)}
                    <option value="All India">All India</option>
                    <option value="International">International</option>
                  </select>
                </Field>
                <Field label="Travel Date"><Input type="date" value={form.date} onChange={e => update('date', e.target.value)} className="h-11 rounded-lg" /></Field>
                <Field label="No. of Travelers">
                  <select value={form.people} onChange={e => update('people', e.target.value)} className="w-full h-11 rounded-lg border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand-orange/40">
                    {['1','2','3','4','5','6+'].map(n => <option key={n} value={n}>{n} {n === '1' ? 'Person' : 'People'}</option>)}
                  </select>
                </Field>
                <div className="md:col-span-2">
                  <Field label="Budget per person">
                    <select value={form.budget} onChange={e => update('budget', e.target.value)} className="w-full h-11 rounded-lg border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand-orange/40">
                      <option value="under-10000">Under ₹10,000</option>
                      <option value="10000-15000">₹10,000 – ₹15,000</option>
                      <option value="15000-25000">₹15,000 – ₹25,000</option>
                      <option value="25000-40000">₹25,000 – ₹40,000</option>
                      <option value="40000+">₹40,000+</option>
                    </select>
                  </Field>
                </div>
              </div>

              <Button type="submit" className="w-full mt-6 bg-brand-orange hover:bg-brand-orangeDark text-white h-12 rounded-xl font-semibold text-[15px] btn-glow">
                Get My Free Quote
              </Button>
              <p className="text-center text-[11.5px] text-brand-blue/55 mt-3">By submitting you agree to our terms. We respect your privacy - no spam ever.</p>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-[12.5px] font-semibold text-brand-blue/75 mb-1.5 block">{label}</span>
      {children}
    </label>
  );
}
